"use client";

import { useCallback, useEffect, useState } from "react";
import { convertToError, flare } from "@flareapp/js";
import { getPushStatus, subscribePush, unsubscribePush } from "@/lib/api";
import { isPushSupported, urlBase64ToUint8Array } from "@/lib/push";

/** Report a swallowed subscribe/unsubscribe failure so it stays diagnosable. */
function reportPushError(error: unknown): void {
  if (process.env.NEXT_PUBLIC_FLARE_KEY) void flare.report(convertToError(error));
}

export type PushSubscribeResult = "subscribed" | "denied" | "error";

/**
 * Resolve the service worker registration, or null when none is available.
 * `navigator.serviceWorker.ready` never settles when no registration ever
 * succeeded (guaranteed in dev — SwRegister only registers in production), so
 * awaiting it unguarded would hang the flow with `busy` stuck true; the race
 * caps the wait instead.
 */
async function swRegistrationOrNull(
  timeoutMs = 10_000,
): Promise<ServiceWorkerRegistration | null> {
  const existing = await navigator.serviceWorker.getRegistration();
  if (existing) return existing;
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((resolve) => {
      setTimeout(() => resolve(null), timeoutMs);
    }),
  ]);
}

/**
 * THIS browser's push subscription, or null (no permission, no registration,
 * or never subscribed here). Deliberately does not wait for `.ready`: a
 * registration still in flight simply reads as "not subscribed yet".
 */
async function getLocalSubscription(): Promise<PushSubscription | null> {
  if (Notification.permission !== "granted") return null;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration) return null;
  return registration.pushManager.getSubscription();
}

export interface PushSubscriptionState {
  /** Initial capability + server-status load finished. */
  loaded: boolean;
  /** Browser supports service worker + PushManager + Notification. */
  supported: boolean;
  /** Server's VAPID public key; null = push disabled server-side (hide UI). */
  vapidPublicKey: string | null;
  /** THIS browser has an active push subscription (per-device semantics). */
  subscribed: boolean;
  /** Current `Notification.permission`; null until known. */
  permission: NotificationPermission | null;
  /** A subscribe/unsubscribe call is in flight. */
  busy: boolean;
  subscribe: () => Promise<PushSubscribeResult>;
  unsubscribe: () => Promise<boolean>;
}

/**
 * The single owner of the web-push subscribe/unsubscribe flow, shared by the
 * settings toggle (PushOptIn) and the matches-page soft prompt (PushPrompt).
 *
 * On mount it checks browser support, fetches the server's VAPID key, and
 * reads the subscribed state from THIS browser (push permission and
 * subscriptions are inherently per-device, so the API's any-device
 * `subscribed` field is ignored). `subscribe` runs the full permission →
 * pushManager.subscribe → POST flow; `unsubscribe` DELETEs the local endpoint
 * and drops the browser subscription. Errors resolve to "error"/false so
 * callers can toast — nothing throws.
 */
export function usePushSubscription(): PushSubscriptionState {
  // Constant per environment; false during SSR, where the UI is hidden anyway
  // (nothing renders until `loaded`, which only flips on the client).
  const supported = isPushSupported();
  const [loaded, setLoaded] = useState(false);
  const [vapidPublicKey, setVapidPublicKey] = useState<string | null>(null);
  const [subscribed, setSubscribed] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission | null>(() =>
    isPushSupported() ? Notification.permission : null,
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) {
      // Nothing to fetch — but "loaded" can only be resolved after mount since
      // support is client-only knowledge (same pattern as AgeGate).
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoaded(true);
      return;
    }
    let alive = true;
    Promise.all([getPushStatus(), getLocalSubscription().catch(() => null)])
      .then(([status, localSubscription]) => {
        if (!alive) return;
        setVapidPublicKey(status.vapidPublicKey);
        setSubscribed(localSubscription !== null);
      })
      .catch(() => {
        // Leave the key null — the UI hides itself, same as push-disabled.
      })
      .finally(() => {
        if (alive) setLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, [supported]);

  const subscribe = useCallback(async (): Promise<PushSubscribeResult> => {
    if (!vapidPublicKey) return "error";
    setBusy(true);
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== "granted") return "denied";

      const registration = await swRegistrationOrNull();
      if (!registration) {
        reportPushError(new Error("push subscribe: no service worker registration"));
        return "error";
      }
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });
      const { endpoint, keys } = subscription.toJSON();
      if (!endpoint || !keys?.p256dh || !keys?.auth) return "error";

      await subscribePush({
        endpoint,
        p256dh: keys.p256dh,
        auth: keys.auth,
        userAgent: navigator.userAgent.slice(0, 512),
      });
      setSubscribed(true);
      return "subscribed";
    } catch (error) {
      reportPushError(error);
      return "error";
    } finally {
      setBusy(false);
    }
  }, [vapidPublicKey]);

  const unsubscribe = useCallback(async (): Promise<boolean> => {
    setBusy(true);
    try {
      const registration = await swRegistrationOrNull();
      if (!registration) return false;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) {
        await unsubscribePush(subscription.endpoint);
        await subscription.unsubscribe();
      }
      setSubscribed(false);
      return true;
    } catch (error) {
      reportPushError(error);
      return false;
    } finally {
      setBusy(false);
    }
  }, []);

  return { loaded, supported, vapidPublicKey, subscribed, permission, busy, subscribe, unsubscribe };
}
